import onDocumentLoaded from "./onDocumentLoaded";
import UrlUtil from "./UrlUtil";

declare global {
	interface Window {
		sessionUUID: string;
	}
}

export default class SessionExistenceChecker {
	private static instance: SessionExistenceChecker;

	private timeoutId = -1;
	private runningCheck = false;

	public constructor() {
		if (SessionExistenceChecker.instance === undefined) {
			SessionExistenceChecker.instance = this;
			onDocumentLoaded(document, this.onDocumentReady);
		}
	}

	private static getExpiredNoticeElement(): HTMLElement | null {
		return document.getElementById("session-expired-notice");
	}

	private onDocumentReady = () => {
		const expiredNoticeElement = SessionExistenceChecker.getExpiredNoticeElement();
		if (expiredNoticeElement !== null) {
			this.queueCheck();
			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState !== "visible") {
					// We don't want to bother checking while the tab is not in focus
					if (this.timeoutId !== -1) {
						clearTimeout(this.timeoutId);
						this.timeoutId = -1;
					}
				} else {
					this.ensureSessionExistence();
				}
			}, false);
			window.addEventListener("pagehide", () => {
				if (document.visibilityState !== "visible") {
					// We don't want to bother checking while the tab is not in focus
					if (this.timeoutId !== -1) {
						clearTimeout(this.timeoutId);
						this.timeoutId = -1;
					}
				}
			}, false);

			expiredNoticeElement.querySelector("a")!.addEventListener("click", event => {
				event.preventDefault();
				event.stopPropagation();
				window.location.reload();
				return false;
			});
		}
	}

	public ensureSessionExistence = () => {
		if (!this.runningCheck) {
			this.runningCheck = true;
			if (this.timeoutId !== -1) {
				clearTimeout(this.timeoutId);
				this.timeoutId = -1;
			}
			if (document.visibilityState === "visible") {
				const sessionUUID: string = window.sessionUUID;
				if (sessionUUID !== undefined) {
					const baseURI = UrlUtil.getInstance().getBaseUrl();
					const oReq = new XMLHttpRequest();
					oReq.open("GET", baseURI + "core-api/session-existence-check?sessionUUID=" + sessionUUID);
					oReq.timeout = 10_000;
					// retrieve data unprocessed as a binary string
					oReq.overrideMimeType("text/plain; charset=x-user-defined");
					oReq.addEventListener("load", () => {
						if (this.validateSessionExistence(oReq)) {
							this.queueCheck();
							SessionExistenceChecker.getExpiredNoticeElement()?.classList.remove("dialogOverlay--open");
							document.removeEventListener("keyup", this.dialogKeyListener);
						} else {
							this.handleSessionDoesNotExist();
						}
					});

					oReq.addEventListener("error", this.handleSessionDoesNotExist);
					oReq.addEventListener("timeout", this.handleSessionDoesNotExist);
					oReq.send();
				} else {
					this.queueCheck();
				}
			} else {
				this.queueCheck();
			}
		}
	}

	private validateSessionExistence = (oReq: XMLHttpRequest) => {
		if (oReq.status === 200 && oReq.responseText !== "not-exists") {
			const firstSpace = oReq.responseText.indexOf(' ');
			if (oReq.responseText.substring(0, firstSpace) !== "exists") {
				return false;
			}
			if (firstSpace > -1 && firstSpace + 1 < oReq.responseText.length) {
				window.sessionUUID = oReq.responseText.substring(firstSpace + 1);
			}
			return true;
		}
		return false;
	}

	private handleSessionDoesNotExist = () => {
		SessionExistenceChecker.getExpiredNoticeElement()?.classList.add("dialogOverlay--open");
		setTimeout(() => document.addEventListener("keyup", this.dialogKeyListener), 500);
		this.queueCheck();
	}

	private dialogKeyListener = (event: KeyboardEvent) => {
		if (event.key === "Esc" || event.key === "Escape" || event.key === "Enter") {
			window.location.reload();
		}
	};

	private queueCheck = () => {
		if (document.visibilityState === "visible") {
			this.timeoutId = setTimeout(this.ensureSessionExistence, 15_000); // Check after 15 seconds
		}
		this.runningCheck = false;
	}
}

new SessionExistenceChecker(); // Self init.
